import React, { useEffect, useState } from 'react';
import { Header } from './Header';
import { BottomPalette } from './BottomPalette';
import { Canvas } from './Canvas';
import { PropertyPanel } from './PropertyPanel';
import { useEditor } from '../store/EditorContext';
import { PreviewModal } from './PreviewModal';
import { Field } from '../types';
import toast from 'react-hot-toast';
import { 
    DndContext, 
    DragEndEvent, 
    DragOverEvent, 
    DragStartEvent, 
    DragOverlay, 
    useSensor, 
    useSensors, 
    PointerSensor, 
    KeyboardSensor, 
    closestCenter,
    defaultDropAnimationSideEffects,
    DropAnimation
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';

export const EditorLayout: React.FC = () => {
  const { state, dispatch, generateId } = useEditor();
  
  // DND State
  const [activeDragItem, setActiveDragItem] = useState<any>(null);
  const [projectedFields, setProjectedFields] = useState<Field[] | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
        activationConstraint: {
            distance: 5,
        }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
      setActiveDragItem(event.active);
      // Initialize projection with current fields
      setProjectedFields([...state.form.fields]);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    const isSidebarItem = active.data.current?.type === 'sidebar-item';

    // If dragging a group, standard reordering
    if (active.data.current?.type === 'group') {
        if (activeId !== overId) {
             dispatch({
                type: 'REORDER_GROUPS',
                payload: { activeId, overId }
            });
        }
        return;
    }

    // Handle Fields (Existing or New)
    if (!projectedFields) return;

    const overIsGroup = state.form.groups.some(g => g.id === overId);
    const overIsRoot = overId === 'root-container';
    
    // Find target group ID
    let targetGroupId: string | undefined = undefined;
    if (overIsGroup) {
        targetGroupId = overId;
    } else if (overIsRoot) {
        targetGroupId = undefined;
    } else {
        // Over a field
        // Try finding in projected first to handle ghost interaction
        const overField = projectedFields.find(f => f.id === overId);
        if (overField) {
            targetGroupId = overField.groupId;
        } else {
            // Check state just in case
            const stateField = state.form.fields.find(f => f.id === overId);
            if (stateField) targetGroupId = stateField.groupId;
        }
    }

    if (isSidebarItem) {
        // We are dragging a NEW item
        // Use a unique ID for the ghost to prevent collision with Sidebar draggable
        const ghostId = `${activeId}-ghost`;
        const sidebarType = active.data.current?.fieldType;
        const sidebarControl = active.data.current?.defaultControl;
        const sidebarLabel = active.data.current?.label;

        // Check if we already have the ghost in projectedFields
        const ghostIndex = projectedFields.findIndex(f => f.id === ghostId);
        
        if (ghostIndex !== -1) {
            // Ghost exists, move it
            
            // If moved to different group
            if (projectedFields[ghostIndex].groupId !== targetGroupId) {
                 const updatedGhost = { ...projectedFields[ghostIndex], groupId: targetGroupId };
                 const newFields = [...projectedFields];
                 newFields.splice(ghostIndex, 1);
                 
                 const insertIndex = projectedFields.findIndex(f => f.id === overId);
                 if (insertIndex !== -1) {
                     newFields.splice(insertIndex, 0, updatedGhost);
                 } else {
                     newFields.push(updatedGhost);
                 }
                 setProjectedFields(newFields);
            } else {
                // Same group, standard sort
                const overIndex = projectedFields.findIndex(f => f.id === overId);
                 if (overIndex !== -1 && ghostIndex !== overIndex) {
                     setProjectedFields(arrayMove(projectedFields, ghostIndex, overIndex));
                 }
            }
        } else {
            // Insert Ghost
            const ghostField: Field = {
                id: ghostId,
                type: sidebarType,
                label: sidebarLabel || 'New Field',
                inputControl: sidebarControl,
                validation: { required: false },
                groupId: targetGroupId
            };
            
            const newFields = [...projectedFields];
            const overIndex = newFields.findIndex(f => f.id === overId);
            
            // Insert at index or append
            if (overIndex !== -1 && !overIsGroup && !overIsRoot) {
                newFields.splice(overIndex, 0, ghostField);
            } else {
                newFields.push(ghostField);
            }
            setProjectedFields(newFields);
        }
    } else {
        // Reordering EXISTING field
        const activeIndex = projectedFields.findIndex(f => f.id === activeId);
        const overIndex = projectedFields.findIndex(f => f.id === overId);

        if (activeIndex !== -1) {
            // Check for group change
            const activeField = projectedFields[activeIndex];
            if (activeField.groupId !== targetGroupId) {
                 // Update group
                 const updatedField = { ...activeField, groupId: targetGroupId };
                 const newFields = [...projectedFields];
                 newFields[activeIndex] = updatedField;
                 
                 // And Move
                 if (overIndex !== -1 && !overIsGroup && !overIsRoot) {
                     setProjectedFields(arrayMove(newFields, activeIndex, overIndex));
                 } else {
                     setProjectedFields(newFields);
                 }
            } else {
                // Same group reorder
                if (overIndex !== -1 && activeIndex !== overIndex) {
                    setProjectedFields(arrayMove(projectedFields, activeIndex, overIndex));
                }
            }
        }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const isSidebarItem = active.data.current?.type === 'sidebar-item';
    
    // Capture projected state before clearing
    const currentProjected = projectedFields;
    
    // Clear drag state
    setProjectedFields(null);
    setActiveDragItem(null);

    if (!over) return;

    if (active.data.current?.type === 'group') {
        return;
    }

    const overId = over.id as string;
    const overIsGroup = state.form.groups.some(g => g.id === overId);
    const overIsRoot = overId === 'root-container';

    // Calculate final target group
    let targetGroupId: string | undefined = undefined;
    if (overIsGroup) {
        targetGroupId = overId;
    } else if (overIsRoot) {
        targetGroupId = undefined;
    } else {
        // Look in state fields
        const overField = state.form.fields.find(f => f.id === overId);
        if (overField) {
            targetGroupId = overField.groupId;
        } else if (currentProjected) {
            // If dropping on the ghost itself (or something not in state), check projection
            const projField = currentProjected.find(f => f.id === overId);
            if (projField) targetGroupId = projField.groupId;
        }
    }

    // Helper to get index within the group from STATE (for existing fields)
    const getGroupFields = (gId: string | undefined) => state.form.fields.filter(f => f.groupId === gId);
    
    if (isSidebarItem) {
        const type = active.data.current?.fieldType;
        const defaultControl = active.data.current?.defaultControl;
        
        // Generate real ID
        const id = generateId(type);
        const newField: Field = {
            id,
            type,
            label: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
            inputControl: defaultControl,
            validation: { required: false },
            groupId: targetGroupId
        };

        // Determine Index
        // If we have a projection, try to find where the ghost ended up relative to the group
        let index = undefined;
        
        if (currentProjected) {
             const ghostId = `${active.id}-ghost`;
             // Filter projection by the target group to find relative index
             const projectedGroupFields = currentProjected.filter(f => f.groupId === targetGroupId);
             const ghostIdx = projectedGroupFields.findIndex(f => f.id === ghostId);
             if (ghostIdx !== -1) {
                 index = ghostIdx;
             }
        }

        dispatch({ type: 'ADD_FIELD', payload: { field: newField, index } });
        toast.success('Field added');

    } else {
        // Move Existing
        const activeId = active.id as string;
        
        // Find index relative to target group
        // Use projection to find final sorted index
        const targetGroupFields = getGroupFields(targetGroupId);
        let newIndex = targetGroupFields.length; 
        
        if (currentProjected) {
             const projGroupFields = currentProjected.filter(f => f.groupId === targetGroupId);
             const fieldIdx = projGroupFields.findIndex(f => f.id === activeId);
             if (fieldIdx !== -1) {
                 newIndex = fieldIdx;
             }
        }

        dispatch({ 
            type: 'MOVE_FIELD', 
            payload: { fieldId: activeId, targetGroupId, newIndex } 
        });
    }
  };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        // Ignore if input is focused
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
            return;
        }

        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        const modKey = isMac ? e.metaKey : e.ctrlKey;

        // Save: Ctrl/Cmd + S
        if (modKey && e.key === 's') {
            e.preventDefault();
            toast.success('Form saved successfully');
            return;
        }

        // Undo: Ctrl/Cmd + Z
        if (modKey && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            dispatch({ type: 'UNDO' });
            return;
        }

        // Redo: Ctrl/Cmd + Shift + Z  OR  Ctrl/Cmd + Y
        if ((modKey && e.key === 'z' && e.shiftKey) || (modKey && e.key === 'y')) {
            e.preventDefault();
            dispatch({ type: 'REDO' });
            return;
        }

        // Duplicate: Ctrl/Cmd + D
        if (modKey && e.key === 'd') {
            e.preventDefault();
            if (state.selectedElementId && state.selectedElementType === 'field') {
                dispatch({ type: 'DUPLICATE_FIELD', payload: state.selectedElementId });
                toast.success('Field duplicated');
            }
            return;
        }

        // Delete: Backspace or Delete
        if (e.key === 'Backspace' || e.key === 'Delete') {
             if (state.selectedElementId) {
                 e.preventDefault();
                 if (state.selectedElementType === 'field') {
                    dispatch({ type: 'DELETE_FIELD', payload: state.selectedElementId });
                    toast('Field deleted', { icon: '🗑️' });
                 } else if (state.selectedElementType === 'group') {
                     dispatch({ type: 'DELETE_GROUP', payload: state.selectedElementId });
                     toast('Group deleted', { icon: '🗑️' });
                 }
             }
        }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.selectedElementId, state.selectedElementType, dispatch]);

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  return (
    <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
    >
        <div className="flex flex-col h-screen overflow-hidden">
        <Header />
        <div className="flex flex-1 overflow-hidden">
            {state.isPreviewMode ? (
                <PreviewModal />
            ) : (
                <>
                    {/* Wrap Canvas and Palette in a relative container */}
                    <div className="flex-1 relative flex flex-col overflow-hidden">
                        <Canvas fields={projectedFields || state.form.fields} />
                        <BottomPalette />
                    </div>
                    <PropertyPanel />
                </>
            )}
        </div>
        
        {createPortal(
            <DragOverlay dropAnimation={dropAnimation}>
                {activeDragItem ? (
                     state.form.groups.find(g => g.id === activeDragItem.id) ? (
                        <div className="p-3 bg-white border-2 border-teal-500 rounded-lg shadow-xl opacity-90 w-[300px]">
                             Group: {state.form.groups.find(g => g.id === activeDragItem.id)?.label}
                        </div>
                     ) : activeDragItem.data.current?.type === 'sidebar-item' ? (
                        <div className="p-3 bg-teal-50 border-2 border-teal-500 rounded-lg shadow-xl opacity-90 flex items-center gap-2">
                             <span className="font-bold text-teal-700 text-sm">New {activeDragItem.data.current.label}</span>
                        </div>
                     ) : (
                        <div className="p-4 bg-white border-2 border-teal-500 rounded-lg shadow-xl opacity-90 w-[300px]">
                             Field: {state.form.fields.find(f => f.id === activeDragItem.id)?.label}
                        </div>
                     )
                ) : null}
            </DragOverlay>,
            document.body
        )}
        </div>
    </DndContext>
  );
};